/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  CheckCircleFilled,
  FilterOutlined,
  HourglassFilled,
  InfoCircleFilled,
  LikeFilled,
  LockFilled,
  RocketOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  StarFilled,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Empty,
  Input,
  message,
  Select,
  Tooltip,
  TreeSelect,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import { listOpportunities, reveal } from 'services/api/domains';
import { createCheckoutSession, upgradeIntent } from 'services/api/users';
import styles from 'styles/components/browseBacklinksOpportunities.module.scss';
import Loader from './loader';
import RequestBacklinkModal from './requestBacklinkModal';
import {
  MAX_CONCURRENT_REQUESTS,
  OPPORTUNITY_SORT,
  OPPORTUNITY_SORT_DIRECTION,
  OPPORTUNITY_SORT_DIRECTION_VALUES,
  OPPORTUNITY_SORT_VALUES,
  PLAN,
  REQUEST_TYPE,
  SESSION_PENDING_PRO_CHECKOUT,
} from 'utils/constants';
import {
  handleDomainAccessError,
  parseOpportunitiesListResponse,
} from 'utils/domainAccessErrors';
import {
  expandCategoryIdsToLeaves,
  hasActiveBacklinkRequest,
  makeCategoriesSelectable,
} from 'utils/utils';
import { useSearchParams } from 'react-router-dom';
import DomainMetrics from './domainMetrics';
import KarmaIcon from './karmaIcon';
import pluralize from 'pluralize';
import CategoryContext from 'services/categoryContext';
import DomainContext from 'services/domainContext';
import ProfileContext from 'services/profileContext';
import { usePostHogFeatureFlag } from 'hooks/usePostHogFeatureFlag';

const SORT_OPTIONS = [
  { value: OPPORTUNITY_SORT.RELEVANCE, label: 'Relevance' },
  { value: OPPORTUNITY_SORT.DR, label: 'Domain Rating' },
  { value: OPPORTUNITY_SORT.RESPONSE_RATE, label: 'Response rate' },
  { value: OPPORTUNITY_SORT.NEW, label: 'Newest' },
];

const BrowseBacklinksOpportunities = ({ domain, refreshDomainDetails }) => {
  const proPlansEnabled = usePostHogFeatureFlag('pro-plan');
  const { profile, updateProfile } = useContext(ProfileContext);
  const isPro = profile?.plan === PLAN.PRO;
  // The API answers LINKEDIN_REQUIRED, which reloads the profile and the
  // domain, which refetches here: don't call it until LinkedIn is connected.
  const hasLinkedin = Boolean(profile?.linkedinProfile);
  const [onlyGoodMatchFilter, setOnlyGoodMatchFilter] = useState(false);
  const [onlyAvailableFilter, setOnlyAvailableFilter] = useState(true);
  const [onlyYCFilter, setOnlyYCFilter] = useState(false);
  const [hideSlowResponders, setHideSlowResponders] = useState(false);
  const [onlyNewFilter, setOnlyNewFilter] = useState(false);
  const [sort, setSort] = useState(OPPORTUNITY_SORT.RELEVANCE);
  const [sortDirection, setSortDirection] = useState(
    OPPORTUNITY_SORT_DIRECTION.DESC
  );

  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  const { categories, categoriesFlat } = useContext(CategoryContext);
  const [opportunities, setOpportunities] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [requestType, setRequestType] = useState(REQUEST_TYPE.GIVE);
  const [searchInputValue, setSearchInputValue] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredOpportunities, setFilteredOpportunities] = useState(null);
  const { updateDomains } = useContext(DomainContext);

  const listOpportunitiesCall = () => {
    if (!hasLinkedin) return;
    setLoadingOpportunities(true);
    listOpportunities({
      domain: domain.name,
      categories: expandCategoryIdsToLeaves(selectedCategories, categories),
      ...(hideSlowResponders ? { hideSlowResponders: true } : {}),
      sort,
      ...(sort === OPPORTUNITY_SORT.RELEVANCE ? {} : { sortDirection }),
    })
      .then((response) => {
        const parsed = parseOpportunitiesListResponse(response.data);
        setOpportunities(parsed.opportunities);
        filterOpportunities(parsed.opportunities);
      })
      .catch((e) => {
        console.error(e);
        setOpportunities([]);
        setFilteredOpportunities([]);
        if (!handleDomainAccessError(e, { updateProfile })) {
          message.error('Could not load opportunities. Please try again.');
        }
      })
      .finally(() => {
        setLoadingOpportunities(false);
      });
  };

  const init = () => {
    if (!opportunities && !loadingOpportunities) {
      listOpportunitiesCall();
    }
  };

  useEffect(() => {
    init();
  }, []);

  // By name: every profile refresh hands us a new domain object.
  useEffect(() => {
    listOpportunitiesCall();
  }, [domain?.name, hasLinkedin]);

  useEffect(() => {
    const categParams = searchParams.get('categories');
    if (categParams?.length > 0) {
      setSelectedCategories(categParams.split(','));
    } else {
      setSelectedCategories((prev) => (prev.length === 0 ? prev : []));
    }

    const isGoodMatchParams = searchParams.get('onlyGoodMatch');
    if (isGoodMatchParams != null && isGoodMatchParams.length > 0) {
      setOnlyGoodMatchFilter(isGoodMatchParams !== 'false');
    }

    // Only sync from URL when present.
    const isAvailableParams = searchParams.get('onlyAvailable');
    if (isAvailableParams != null && isAvailableParams.length > 0) {
      setOnlyAvailableFilter(isAvailableParams !== 'false');
    }

    const onlyYCParams = searchParams.get('onlyYC');
    if (onlyYCParams != null && onlyYCParams.length > 0) {
      setOnlyYCFilter(onlyYCParams !== 'false');
    }

    const hideSlowParams = searchParams.get('hideSlowResponders');
    if (hideSlowParams != null && hideSlowParams.length > 0) {
      setHideSlowResponders(hideSlowParams !== 'false');
    }

    const sortParam = searchParams.get('sort');
    if (OPPORTUNITY_SORT_VALUES.includes(sortParam)) {
      setSort(sortParam);
    } else {
      setSort((prev) =>
        prev === OPPORTUNITY_SORT.RELEVANCE ? prev : OPPORTUNITY_SORT.RELEVANCE
      );
    }

    const sortDirectionParam = searchParams.get('sortDirection');
    if (OPPORTUNITY_SORT_DIRECTION_VALUES.includes(sortDirectionParam)) {
      setSortDirection(sortDirectionParam);
    } else {
      setSortDirection((prev) =>
        prev === OPPORTUNITY_SORT_DIRECTION.DESC
          ? prev
          : OPPORTUNITY_SORT_DIRECTION.DESC
      );
    }

    const onlyNewParams = searchParams.get('onlyNew');
    if (onlyNewParams != null && onlyNewParams.length > 0) {
      setOnlyNewFilter(onlyNewParams !== 'false');
    } else {
      setOnlyNewFilter((prev) => (prev === false ? prev : false));
    }
  }, [searchParams]);

  const onChange = (value) => {
    setSearchParams((prev) => {
      prev.set('categories', value);
      return prev;
    });
  };

  useEffect(() => {
    listOpportunitiesCall();
  }, [selectedCategories, hideSlowResponders, sort, sortDirection]);

  useEffect(() => {
    filterOpportunities();
  }, [
    searchInputValue,
    onlyGoodMatchFilter,
    onlyAvailableFilter,
    onlyYCFilter,
    onlyNewFilter,
  ]);

  const filterOpportunities = (opps = null) => {
    // Server owns ordering (including paused-last). Do not .sort() here.
    let filtered = opps || opportunities;

    if (searchInputValue && searchInputValue.length > 0) {
      filtered = filtered?.filter(
        (e) => `${e.description?.toLowerCase()}`.search(searchInputValue) !== -1
      );
    }
    if (onlyGoodMatchFilter) {
      filtered = filtered?.filter((e) => getIsGoodMatch(e));
    }
    if (onlyAvailableFilter) {
      filtered = filtered?.filter((e) => getIsAvailable(e));
    }
    if (onlyYCFilter && domain.isYC) {
      filtered = filtered?.filter((e) => e.isYC);
    }
    if (onlyNewFilter) {
      filtered = filtered?.filter((e) => e.isNew);
    }
    setFilteredOpportunities(filtered);
  };

  // Locked cards (can't afford the Ask) stay visible: they carry the upsell.
  const getIsAvailable = (opportunity) =>
    (opportunity.canGive || opportunity.canReceive || opportunity.locked) &&
    !hasActiveBacklinkRequest(opportunity);

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const showProUpsell = proPlansEnabled && !isPro;
  const lockedCount =
    filteredOpportunities?.filter((o) => o.locked).length || 0;

  const startProCheckout = async () => {
    upgradeIntent('locked-card').catch(() => {});
    setCheckoutLoading(true);
    try {
      const { data } = await createCheckoutSession();
      sessionStorage.setItem(SESSION_PENDING_PRO_CHECKOUT, '1');
      window.location.href = data.url;
    } catch (err) {
      message.error('Could not start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const onChangeSearchInputValue = (e) => {
    setSearchInputValue(e.target.value);
  };

  const openRequestModal = (dom, type) => {
    setRequestType(type);
    setRequestModalOpen(dom);
  };
  const setRequestModalClose = () => {
    setRequestModalOpen(null);
    listOpportunitiesCall();
  };

  const ownDr = domain?.metrics?.authority?.dr;
  const getIsGoodMatch = (opportunity) => {
    const categoriesInCommon = domain.categories.filter((value) =>
      opportunity.categories.includes(value)
    );
    const hasCategoryMatch = categoriesInCommon.length > 0;
    const oppDr = opportunity?.metrics?.authority?.dr;
    if (ownDr == null || oppDr == null) {
      return hasCategoryMatch;
    }
    const drDifference = Math.abs(ownDr - oppDr);
    const hasLanguageMatch =
      domain.language &&
      opportunity.language &&
      domain.language === opportunity.language;
    const maxDrTolerance = hasLanguageMatch ? 15 : 10;

    if (drDifference <= maxDrTolerance && hasCategoryMatch) {
      return true;
    }
    return false;
  };

  const onChangeOnlyGoodMatch = (e) => {
    // setOnlyGoodMatchFilter(!onlyGoodMatchFilter);
    setSearchParams((prev) => {
      prev.set('onlyGoodMatch', e.target.checked);
      return prev;
    });
  };

  const onChangeOnlyAvailable = (e) => {
    const checked =
      typeof e?.target?.checked === 'boolean'
        ? e.target.checked
        : !onlyAvailableFilter;
    setOnlyAvailableFilter(checked);
    setSearchParams((prev) => {
      prev.set('onlyAvailable', String(checked));
      return prev;
    });
  };

  const onChangeOnlyYC = (e) => {
    // setOnlyGoodMatchFilter(!onlyGoodMatchFilter);
    setSearchParams((prev) => {
      prev.set('onlyYC', e.target.checked);
      return prev;
    });
  };

  const onChangeHideSlowResponders = (e) => {
    setSearchParams((prev) => {
      prev.set('hideSlowResponders', e.target.checked);
      return prev;
    });
  };

  const onChangeSort = (value) => {
    setSearchParams((prev) => {
      if (value === OPPORTUNITY_SORT.RELEVANCE) {
        prev.delete('sort');
        prev.delete('sortDirection');
      } else {
        prev.set('sort', value);
        if (!prev.get('sortDirection')) {
          prev.set('sortDirection', OPPORTUNITY_SORT_DIRECTION.DESC);
        }
      }
      return prev;
    });
  };

  const onToggleSortDirection = () => {
    if (sort === OPPORTUNITY_SORT.RELEVANCE) return;
    const next =
      sortDirection === OPPORTUNITY_SORT_DIRECTION.DESC
        ? OPPORTUNITY_SORT_DIRECTION.ASC
        : OPPORTUNITY_SORT_DIRECTION.DESC;
    setSearchParams((prev) => {
      prev.set('sort', sort);
      prev.set('sortDirection', next);
      return prev;
    });
  };

  const onChangeOnlyNew = (e) => {
    const checked =
      typeof e?.target?.checked === 'boolean'
        ? e.target.checked
        : !onlyNewFilter;
    setOnlyNewFilter(checked);
    setSearchParams((prev) => {
      prev.set('onlyNew', String(checked));
      return prev;
    });
  };

  const revealDomain = (oppId) => {
    reveal({ domain: domain.name, opportunityId: oppId })
      .then((res) => {
        const currentRevealsRemaining = domain.reveals.remaining;
        updateDomains();
        setTimeout(() => {
          refreshDomainDetails();
          listOpportunitiesCall();
          setTimeout(() => {
            message.success(
              `Domain revealed successfully. You have ${
                currentRevealsRemaining > 1 ? currentRevealsRemaining - 1 : 'no'
              } reveals remaining.`
            );
          }, 300);
        }, 500);
      })
      .catch((e) => {
        console.error(e);
        if (handleDomainAccessError(e, { updateProfile })) {
          return;
        }
        if (e.response.data.error === 'CROSS_TIER_NOT_ALLOWED') {
          if (proPlansEnabled && !isPro) {
            message.info(
              'This domain is in a different DR tier. Upgrade to Pro to access all tiers.'
            );
          } else {
            message.error('This domain is no longer available for exchange.');
          }
          listOpportunitiesCall();
        } else if (e.response.data.error === 'REVEAL_THRESHOLD_REACHED') {
          message.error(
            'You have reached the reveal threshold. Exchange a backlink to reset the quota.'
          );
        } else if (e.response.data.error === 'NOT_ENOUGH_POINTS') {
          message.error("You don't have enough points to reveal this domain.");
        }
      });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.filters}>
        <div className={styles.filterRowSearch}>
          <div className={styles.filter}>
            <TreeSelect
              className={styles.categorySelectFilter}
              allowClear={true}
              prefix={<FilterOutlined />}
              onChange={onChange}
              multiple
              style={{ width: 400, maxWidth: '100%' }}
              fieldNames={{
                children: 'subCategories',
                value: '_id',
                label: 'name',
              }}
              placeholder="Select a category"
              showSearch
              treeData={makeCategoriesSelectable(categories)}
              filterTreeNode={(input, node) =>
                node.name.toLowerCase().includes(input)
              }
              value={selectedCategories}
              disabled={!opportunities}
            />
          </div>
          <div className={styles.filter}>
            <div
              className={styles.onlyMyCategoriesButton}
              onClick={() => {
                setSearchParams((prev) => {
                  prev.set('categories', domain.categories);
                  return prev;
                });
              }}
            >
              <FilterOutlined style={{ marginRight: 4 }} />
              Only my categories
            </div>
          </div>
          <div className={`${styles.filter} ${styles.searchFilter}`}>
            <Input
              placeholder="Search keywords from description"
              value={searchInputValue}
              onChange={onChangeSearchInputValue}
              prefix={<SearchOutlined />}
              className={styles.searchInput}
              allowClear={true}
            />
          </div>
        </div>

        <div className={styles.filterRow}>
        <div className={`${styles.filter} ${styles.sortControls}`}>
            <Select
              className={styles.sortSelect}
              value={sort}
              onChange={onChangeSort}
              options={SORT_OPTIONS}
              popupMatchSelectWidth={false}
              disabled={!opportunities}
            />
            {sort !== OPPORTUNITY_SORT.RELEVANCE && (
              <Tooltip
                title={
                  sort === OPPORTUNITY_SORT.NEW
                    ? sortDirection === OPPORTUNITY_SORT_DIRECTION.DESC
                      ? 'Newest first'
                      : 'Oldest first'
                    : sortDirection === OPPORTUNITY_SORT_DIRECTION.DESC
                    ? 'Highest first'
                    : 'Lowest first'
                }
              >
                <Button
                  className={styles.sortDirectionButton}
                  onClick={onToggleSortDirection}
                  aria-label="Toggle sort direction"
                  icon={
                    sortDirection === OPPORTUNITY_SORT_DIRECTION.DESC ? (
                      <SortDescendingOutlined />
                    ) : (
                      <SortAscendingOutlined />
                    )
                  }
                />
              </Tooltip>
            )}
          </div>
        <div className={styles.filter}>
          <Tooltip
            title={
              <div>
                Good match domains have at least one category in common and has
                a DR close to yours.
              </div>
            }
          >
            <div
              className={styles.onlyGoodMatchButton}
              onClick={onChangeOnlyGoodMatch}
            >
              <Checkbox
                checked={onlyGoodMatchFilter}
                style={{ marginRight: 6 }}
              />
              <LikeFilled style={{ marginRight: 4 }} />
              Only Good Matches
            </div>
          </Tooltip>
        </div>
        <div className={styles.filter}>
          <div
            className={styles.onlyAvailableButton}
            onClick={onChangeOnlyAvailable}
          >
            <Checkbox
              checked={onlyAvailableFilter}
              style={{ marginRight: 6 }}
            />
            <CheckCircleFilled style={{ marginRight: 4 }} />
            Only Available
          </div>
        </div>
        {domain.isYC && (
          <div className={styles.filter}>
            <Tooltip title={<div>Filter only YC companies.</div>}>
              <div className={styles.onlyYCButton} onClick={onChangeOnlyYC}>
                <Checkbox checked={onlyYCFilter} style={{ marginRight: 6 }} />
                <img src="/img/yc.png" className={styles.ycLogo} alt="yc" />
                Only YC companies&nbsp;
                <InfoCircleFilled />
              </div>
            </Tooltip>
          </div>
        )}
        <div className={styles.filter}>
          <Tooltip title={<div>Hide domains that are slow to respond to requests.</div>}>
            <div
              className={styles.hideSlowRespondersButton}
              onClick={onChangeHideSlowResponders}
            >
              <Checkbox
                checked={hideSlowResponders}
                style={{ marginRight: 6 }}
              />
              <HourglassFilled style={{ marginRight: 4 }} />
              Hide Slow Responders
            </div>
          </Tooltip>
        </div>
        <div className={styles.filter}>
          <Tooltip title={<div>Show domains that joined in the last 14 days.</div>}>
            <div className={styles.onlyNewButton} onClick={onChangeOnlyNew}>
              <Checkbox checked={onlyNewFilter} style={{ marginRight: 6 }} />
              <StarFilled style={{ marginRight: 4 }} />
              New only
            </div>
          </Tooltip>
        </div>
        <div className={styles.filterRight}>
          {filteredOpportunities && (
            <div className={styles.filter}>
              <span>{`${filteredOpportunities.length} ${pluralize(
                'result',
                filteredOpportunities.length
              )}${
                showProUpsell && lockedCount > 0
                  ? ` · ${lockedCount} locked on Free`
                  : ''
              }`}</span>
            </div>
          )}
        </div>
        </div>
      </div>
      {showProUpsell && lockedCount > 0 && (
        <div className={styles.poolUpsell}>
          <div>
            <strong>Limited pool on Free</strong>
            <p>Upgrade to Pro for 200 KarmaPoints every month.</p>
          </div>
          <Button
            type="primary"
            icon={<RocketOutlined />}
            loading={checkoutLoading}
            onClick={startProCheckout}
          >
            Upgrade to Pro
          </Button>
        </div>
      )}
      {!hasLinkedin && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Connect your LinkedIn to browse opportunities."
        />
      )}
      {filteredOpportunities?.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            opportunities?.length === 0 ? (
              <div>
                <div>No matching opportunities available yet.</div>
                <div style={{ marginTop: 8, color: '#888', fontSize: 13 }}>
                  New matching domains are added weekly — check back soon.
                </div>
              </div>
            ) : (
              'No opportunities match your current filters, try adjusting them.'
            )
          }
        />
      )}
      {(() => {
        const list = filteredOpportunities || [];

        const renderOpportunityCard = (element) => {
          const categoriesDomain = categoriesFlat
            ? element.categories.map((r) =>
                categoriesFlat?.find((c) => c._id === r)
              )
            : element.categories;

          const isGoodMatch = getIsGoodMatch(element);
          const elementDr = element?.metrics?.authority?.dr;

          return (
            <div
              className={`${styles.vignetteWrapper}${
                element.locked ? ` ${styles.isLocked}` : ''
              }`}
              key={element._id}
            >
              <div className={styles.vignetteInner}>
                {element.locked && (
                  <div className={styles.lockOverlay}>
                    <LockFilled />
                    {showProUpsell ? (
                      <>
                        <span className={styles.lockTitle}>Pro</span>
                        <p>Upgrade to request from this domain.</p>
                        <Button
                          type="primary"
                          size="small"
                          loading={checkoutLoading}
                          onClick={startProCheckout}
                        >
                          Upgrade to Pro
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className={styles.lockTitle}>
                          Not enough Karma
                        </span>
                        <p>Give a backlink to earn Karma.</p>
                      </>
                    )}
                  </div>
                )}
                <div className={styles.domainDetailsWrapper}>
                  <DomainMetrics
                    revealDomain={revealDomain}
                    domain={element}
                    categories={categoriesDomain}
                    isGoodMatch={isGoodMatch}
                    canSeeYCCompanies={domain.isYC}
                    revealsRemaining={domain?.reveals?.remaining}
                  />
                </div>

                <div className={styles.vignetteCTAs}>
                  <div className={styles.vignetteCTA}>
                    <div className={styles.vignetteCTAInner}>
                      {(() => {
                        const notEnoughPoints =
                          elementDr != null && elementDr > domain.points;
                        const isDisabled =
                          element.locked ||
                          hasActiveBacklinkRequest(element) ||
                          (!element.canReceive && !notEnoughPoints);

                        return (
                          <Tooltip
                            title={
                              hasActiveBacklinkRequest(element)
                                ? 'You already exchanged backlinks with this domain'
                                : !element.canReceive && !notEnoughPoints
                                ? 'You reached your maximum active requests (2)'
                                : ''
                            }
                          >
                            <Button
                              block
                              disabled={isDisabled}
                              onClick={() =>
                                openRequestModal(element, REQUEST_TYPE.ASK)
                              }
                            >
                              Ask
                              {elementDr != null && (
                                <b>{`  -${elementDr} `}</b>
                              )}
                              <KarmaIcon fade={isDisabled} />
                            </Button>
                          </Tooltip>
                        );
                      })()}
                    </div>
                  </div>
                  <div className={styles.vignetteCTA}>
                    <div className={styles.vignetteCTAInner}>
                      {(() => {
                        const isDisabled =
                          !element.canGive ||
                          hasActiveBacklinkRequest(element) ||
                          domain.pendingGiveRequestsCount >=
                            MAX_CONCURRENT_REQUESTS;

                        return (
                          <Tooltip
                            title={
                              hasActiveBacklinkRequest(element)
                                ? 'You already exchanged backlinks with this domain'
                                : domain.pendingGiveRequestsCount >=
                                  MAX_CONCURRENT_REQUESTS
                                ? 'You have reached your maximum active requests (2/2)'
                                : ownDr == null
                                ? 'You can give backlinks once your Domain Rating is in.'
                                : !element.canGive
                                ? "This website can't receive requests at the moment. They may not have enough points."
                                : ''
                            }
                          >
                            <Button
                              type="primary"
                              block
                              disabled={isDisabled}
                              onClick={() =>
                                openRequestModal(element, REQUEST_TYPE.GIVE)
                              }
                            >
                              Give
                              {ownDr != null && <b>{`  +${ownDr} `}</b>}
                              <KarmaIcon fade={isDisabled} />
                            </Button>
                          </Tooltip>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        };

        return (
          <>
      <div className={styles.list}>
        {list.map(renderOpportunityCard)}
        <RequestBacklinkModal
          domain={domain}
          opportunity={requestModalOpen}
          isOpen={!!requestModalOpen}
          onClose={setRequestModalClose}
          categories={categoriesFlat}
          type={requestType}
          refreshDomainDetails={refreshDomainDetails}
          proPlansEnabled={proPlansEnabled}
          isPro={isPro}
        />
        {loadingOpportunities && <Loader />}
      </div>
          </>
        );
      })()}
    </div>
  );
};

export default BrowseBacklinksOpportunities;
